import s from './InChatView.module.css';
import '../styles.css';
import ChatMessage, { MessageTextView, MessageView } from "@common/Chat/components/ChatMessage/ChatMessage";
import React, {useEffect, useRef, useState} from "react";
import {chatApi, helpdeskAPI, usersAPI} from "@/api";
import sendIcon from '../icons/3682321.png'
import {
    AlarmIcon,
    ArrowLeftIcon,
    CloseIcon,
    DotsIcon,
    IconSearch,
    MicrophoneIcon,
    MicrophoneIconActive,
    PaperClipIcon,
    ScheduleIcon
} from "@/utils/icons";
import classNames from "classnames";
import { StarFilled, StarOutlined } from '@ant-design/icons';
import {useDispatch, useSelector} from "react-redux";
import {selectProfile} from "@store/profile.reducer";
import {useInputNew} from "@UI/InputNew/useInputNew";
import * as InputNew from "@UI/InputNew/Input";
import {getRightWord} from "@common/Chat/helpers";
import { useChatContext } from '../ChatContext';
import Popup from "@common/Chat/components/Popup/Popup";
import UserImage from "@common/Chat/components/UserImage/UserImage";
import { createDate } from '@/helpers/date';
import websocket from '@/utils/websocket';
import useIsTechSupport from '../SupportChatButton/useIsTechSupport';
import EditEntityModal from '@modals/EditEntityModal/EditEntityModal';
import { useGetDataForEditEntityModal } from '@modals/EditEntityModal/hooks/useGetDataForEditEntityModal';
import { convertURLs } from '../helpers/convertURLs';
import { roleAdminInChat, roleReaderInChat, rolesChatParticipants } from '../constants';
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper';
import { openModal } from '@store/modalsSlise';
import TYPES from '@modals/TYPES';
import { useCancelToken } from '@/hooks/useCancelToken';
import { DelayedMessage } from '../components/ChatMessage/DelayedMessage';
import Preloader from '@UI/Preloader';
import useAudioRecorder, { nameAudioFile, nameAudioExtension } from '@/hooks/useAudioRecorder';
import useRoles from '@/hooks/useRoles';
import ChatTextInput from './components/ChatTextInput/ChatTextInput';
import { useChatMessages } from './hooks/useChatMessages';
import { useChatTopics } from './hooks/useChatTopics';
import { scrollMessageToCenter } from './utils/scroll';
import MessageSearchPanel from './components/MessageSearchPanel/MessageSearchPanel';
import ChatTopics from './components/ChatTopics/ChatTopics';
import { formateName } from '@/helpers/formateName';
import { useAddAlert } from '@/hooks/useAddAlert';
import {
    getSupportTicketFromEvent,
    getSupportTicketManagerName,
    getSupportTicketText,
} from '../helpers/supportTickets';
import { DEFAULT_HELPDESK_SETTINGS, fetchHelpdeskSettings } from '@/helpers/helpdeskSettings';
import CompactSelect from '../components/CompactSelect/CompactSelect';
import {
    applyCensorshipToText,
    CENSORSHIP_MODES,
    getCensorshipMatches,
    parseCensorshipWords,
} from '@/helpers/censorship';
import { readPdfText } from '@/helpers/pdf';
import ForwardMessagesModal from './components/ForwardMessagesModal';

const parseTechSupportUsers = (value) => {
    try {
        const users = value ? JSON.parse(value) : [];
        return Array.isArray(users) ? users : [];
    } catch {
        return [];
    }
};

const getChatMessagePreview = (message) => {
    const rawMessage = message?.message;
    const text = typeof rawMessage === 'string'
        ? rawMessage.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
        : '';

    if (text) return text;

    const firstFile = Array.isArray(message?.files) ? message.files[0] : null;
    const fileType = String(firstFile?.type || firstFile?.mime_type || '');

    if (fileType.includes('audio')) return 'Голосовое сообщение';
    if (fileType.includes('image')) return 'Изображение';
    if (firstFile) return firstFile?.original || firstFile?.name || 'Файл';

    return 'Сообщение';
};

const InChatView = ({setPage, chat, activeUsers, height, params, isFullPage = false}) => {
    const normalizeChatMessage = React.useCallback((message) => ({
        ...message,
        message: convertURLs(message?.message),
    }), []);

    const [editChildren, setEditChildren] = useState(null);
    const profile = useSelector(selectProfile);
    const addAlert = useAddAlert();
    const { isChildrenOrParent } = useRoles();
    const { isTechSupport, chatIsDisabledButTechnicalSupportIsEnabled } = useIsTechSupport()
    const isHideParticipantsListInGroupChatsForChildrenAndParentStore = useSelector(
        (store) => store.settings.isHideParticipantsListInGroupChatsForChildrenAndParent
    );
    const isSupportManagersDoNotAutoReadSupportChatsStore = useSelector(
        (store) => store.settings.isSupportManagersDoNotAutoReadSupportChats
    );
    const censorshipEnabled = useSelector((store) => store.settings.censorshipEnabled === '1');
    const censorshipMode = useSelector((store) => store.settings.censorshipMode ?? CENSORSHIP_MODES.BLOCK);
    const censorshipWordsStore = useSelector((store) => store.settings.censorshipForbiddenWords);
    const censorshipWords = React.useMemo(
        () => parseCensorshipWords(censorshipWordsStore),
        [censorshipWordsStore],
    );
    const techSupportUsersSetting = useSelector((store) => store.settings.techSupportUsers);
    const techSupportUserIds = React.useMemo(() => parseTechSupportUsers(techSupportUsersSetting), [techSupportUsersSetting]);
    const chatContext = useChatContext();
    const setUnreadSupportMessagesCount = chatContext?.setUnreadSupportMessagesCount;
    const [action, setAction] = useState('')
    const [isVisiblePopup, setIsVisiblePopup] = useState(false);
    const [data, setData] = useState(null);
    const [supportStatuses, setSupportStatuses] = useState([]);
    const [supportManagers, setSupportManagers] = useState([]);
    const [isLoadingInitialMessages, setIsLoadingInitialMessages] = useState(true);
    const [message, setMessage] = useState('');
    const [isActive, setIsActive] = useState(false);
    const [answerTo, setAnswerTo] = useState(null);
    const [answerAuthor, setAnswerAuthor] = useState(null)
    const messageEndRef = useRef(null)
    const messagesContainerRef = useRef(null)
    const [file, setFile] = useState(null);
    const [isNeedUpdate, setIsNeedUpdate] = useState(false);
    const [saving, setSaving] = useState(false);
    const [progress, setProgress] = useState(0);
    const [highlightedMessageId, setHighlightedMessageId] = useState(null);
    const [highlightedMessagesFromId, setHighlightedMessagesFromId] = useState(null);
    const [activeTopPanel, setActiveTopPanel] = useState(null);
    const [messageToForward, setMessageToForward] = useState(null);
    const pendingTopicMessageRef = useRef(null);
    const pendingHighlightFromMessageIdRef = useRef(null);
    const getChatRequestIdRef = useRef(0);
    const socketCatchUpNeededRef = useRef(false);
    const titleChat = (data?.members?.length === 2 && data.type != 'group') ? (data?.members.find(member => member.name !== profile.name))?.name : data?.name;
    const idInterlocutor = data?.type === 'support' ? (data?.members.find(member => member?.isCreator === 1))?.user_id : null;
    const chatTextInputRef = useRef(null);
    const highlightTimeoutRef = useRef(null);
    const {
        getCancelTokenSource: getGetChatCancelTokenSource,
        cancel: cancelGetChatRequest,
    } = useCancelToken();
    const { getCancelTokenSource, cancel: cancelRequest, getCurrentSource } = useCancelToken();
    const { dataForEditEntityModal } = useGetDataForEditEntityModal({priorities: ['student', 'parent', 'employee'], userId: editChildren})
    const [delayedMessages, setDelayedMessages] = useState([]);
    const meInChat = data?.members?.find(el => el.user_id === profile.id);
    const isChatReader = rolesChatParticipants[meInChat?.role] === roleReaderInChat;
    const isChatAdmin = rolesChatParticipants[meInChat?.role] === roleAdminInChat;
    const isGroupChat = data?.type === 'group';
    const isLessonChat = params?.selectedTab === 'lesson' || data?.scope === 'lesson';
    const shouldHideLessonBackButton = isLessonChat;
    const shouldHideParticipantsSettingsMenuItem =
        isHideParticipantsListInGroupChatsForChildrenAndParentStore === '1' &&
        isChildrenOrParent &&
        isGroupChat;
    const shouldDisableAutoReadForCurrentChat =
        data?.type === 'support' &&
        isTechSupport &&
        isSupportManagersDoNotAutoReadSupportChatsStore === '1';

    useEffect(() => {
        if (data?.type !== 'support' || !isTechSupport) return;

        chatApi.getSupportChatStatuses().then((response) => {
            if (response?.data?.status === 'success') {
                setSupportStatuses(Array.isArray(response?.data?.data) ? response.data.data : []);
            }
        });
    }, [data?.type, isTechSupport]);

    useEffect(() => {
        if (data?.type !== 'support' || !isTechSupport) return;

        if (!techSupportUserIds.length) {
            setSupportManagers([]);
            return;
        }

        usersAPI.getUsers({
            filter: ['[role][neq]=children'],
            pageSize: false,
        }).then((response) => {
            const users = Array.isArray(response?.data?.data) ? response.data.data : [];

            setSupportManagers(
                users.filter((user) => techSupportUserIds.some((id) => Number(id) === Number(user?.id)))
            );
        });
    }, [data?.type, isTechSupport, techSupportUserIds]);

    const {
        preparedTopics,
        activeTopicId,
        isLoadingTopics,
        isTopicsVisible,
        loadTopics,
        selectTopic,
        createTopic,
        renameTopic,
        deleteTopic,
        changeTopicMessagesCount,
        applyTopicUnreadMessages,
    } = useChatTopics({
        chatId: chat,
        isGroupChat,
        isChatAdmin,
    });
    const {
        dataMessages,
        messagesViewMode,
        isLoadingOlderMessages,
        isLoadingMessageContext,
        hasOlderMessages,
        hasNewerMessages,
        pendingScrollTargetId,
        isOlderMessagesScrollEnabled,
        appendIncomingMessage,
        appendUploadedFile,
        appendTempMessage,
        replaceTempMessage,
        removeMessage,
        updateMessageReactions,
        refreshLatestMessages,
        openMessageContext,
        returnToLatestMessages,
        handleMessagesScroll,
        clearPendingScrollTarget,
        enableOlderMessagesScroll,
        disableOlderMessagesScroll,
    } = useChatMessages({
        chatId: chat,
        topicId: activeTopicId,
        containerRef: messagesContainerRef,
        normalizeMessage: normalizeChatMessage,
    });

    const [loadingDelayedMessages, setLoadingDelayedMessages] = useState(false);
    const getDelayedMessages = () => {
        if(!chat) return;
        setLoadingDelayedMessages(true);
        const delayedMessageParams = {
            chat_id: chat,
            ...(activeTopicId === null || activeTopicId === undefined ? {} : { topic_id: activeTopicId }),
        };

        chatApi.getScheduledMessages(delayedMessageParams)
            .then(({data})=>{
                if(data?.data?.status === 'success' && Array.isArray(data?.data?.items)){
                    const filteredItems = data?.data?.items?.filter((item) => {
                        const itemTopicId = item?.topic_id ?? null;

                        if (activeTopicId === null || activeTopicId === undefined) {
                            return itemTopicId === null || itemTopicId === undefined;
                        }

                        return String(itemTopicId) === String(activeTopicId);
                    });

                    setDelayedMessages(filteredItems?.map((item) => ({...item, message: convertURLs(item.message)})))
                }
                else{
                    setDelayedMessages([])
                }
            })
            .finally(() => setLoadingDelayedMessages(false))
    }
  
    useEffect(()=>{
        getDelayedMessages()
    }, [chat, activeTopicId])

    const [isShowDelayedMessages, setIsShowDelayedMessages] = useState(false);
    const toggleIsShowDelayedMessages = () => {
        setIsShowDelayedMessages(prev => !prev)
        setTimeout(() => {
            scrollToBottom()
        }, [500])
    }

    const thereIsDelayedMessages = (!!delayedMessages?.length && !message?.trim()) || isShowDelayedMessages;

    // Функция для сброса счетчика поддержки при чтении сообщений в чате поддержки
    const resetSupportUnreadCountIfNeeded = () => {
        if (
            data?.type === 'support' &&
            setUnreadSupportMessagesCount &&
            !shouldDisableAutoReadForCurrentChat
        ) {
            setUnreadSupportMessagesCount(0);
        }
    }

    const getActiveChatParams = React.useCallback(() => (
        activeTopicId === null || activeTopicId === undefined
            ? {}
            : { topic_id: activeTopicId }
    ), [activeTopicId]);

    useEffect(() => {
        setAnswerTo(null);
        setAnswerAuthor(null);
        setActiveTopPanel(null);
        setHighlightedMessageId(null);
        setHighlightedMessagesFromId(null);
    }, [activeTopicId]);

    useEffect(() => {
        pendingTopicMessageRef.current = null;
        pendingHighlightFromMessageIdRef.current = null;
        setMessageToForward(null);
    }, [chat]);

    useEffect(() => {
        let isActiveRequest = true;
        const requestId = getChatRequestIdRef.current + 1;
        getChatRequestIdRef.current = requestId;
        const currentPendingMessage = pendingTopicMessageRef.current;
        const shouldOpenPendingMessage =
            !!currentPendingMessage &&
            String(currentPendingMessage?.topicId ?? null) === String(activeTopicId ?? null);
        const cancelTokenSource = getGetChatCancelTokenSource();

        disableOlderMessagesScroll();
        setIsLoadingInitialMessages(true);

        const loadChat = async () => {
            const res = await chatApi.getChat(chat, getActiveChatParams(), cancelTokenSource);
            if (!isActiveRequest || getChatRequestIdRef.current !== requestId) return;

            const data = {
                    ...res?.data,
            }
            setData({
                ...data,
                messages: undefined
            })
            setIsNeedUpdate(false)
            // Сбрасываем счетчик поддержки при загрузке чата поддержки
            if (
                res?.data?.type === 'support' &&
                setUnreadSupportMessagesCount &&
                !shouldDisableAutoReadForCurrentChat
            ) {
                setUnreadSupportMessagesCount(0);
            }

            const firstUnreadMessageId = res?.data?.firstUnreadMessageId ?? null;
            const hasUnreadMessages = Boolean(res?.data?.hasUnreadMessages);
            const shouldOpenUnreadMessage =
                !shouldOpenPendingMessage &&
                hasUnreadMessages &&
                !!firstUnreadMessageId;

            if (shouldOpenPendingMessage) {
                pendingHighlightFromMessageIdRef.current = null;
                await openMessageContext(
                    currentPendingMessage.messageId,
                    currentPendingMessage.topicId ?? null,
                );
            }
            else if (shouldOpenUnreadMessage) {
                pendingHighlightFromMessageIdRef.current = firstUnreadMessageId;
                await openMessageContext(firstUnreadMessageId, activeTopicId);
            }
            else{
                pendingHighlightFromMessageIdRef.current = null;
                await returnToLatestMessages(activeTopicId);
            }

            if (!isActiveRequest || getChatRequestIdRef.current !== requestId) return;

            if (shouldOpenPendingMessage) {
                pendingTopicMessageRef.current = null;
            }

            if (!shouldDisableAutoReadForCurrentChat) {
                await chatApi.readAllChatMessages(chat);
            }
        }

        loadChat().finally(() => {
            if (isActiveRequest && getChatRequestIdRef.current === requestId) {
                setIsLoadingInitialMessages(false);
            }
        })

        return () => {
            isActiveRequest = false;
            if (getChatRequestIdRef.current === requestId) {
                cancelGetChatRequest('Смена чата или темы');
            }
        };
    }, [
        activeTopicId,
        chat,
        disableOlderMessagesScroll,
        getActiveChatParams,
        openMessageContext,
        returnToLatestMessages,
        setUnreadSupportMessagesCount,
        shouldDisableAutoReadForCurrentChat,
    ]);

    useEffect(() => {
        if (!isNeedUpdate) return;

        let isActiveRequest = true;
        const requestId = getChatRequestIdRef.current + 1;
        getChatRequestIdRef.current = requestId;
        const cancelTokenSource = getGetChatCancelTokenSource();

        chatApi.getChat(chat, getActiveChatParams(), cancelTokenSource).then((res) => {
            if (!isActiveRequest || getChatRequestIdRef.current !== requestId) return;

            const data = {
                ...res?.data,
            }

            setData({
                ...data,
                messages: undefined
            })
        }).finally(() => {
            if (isActiveRequest && getChatRequestIdRef.current === requestId) {
                setIsNeedUpdate(false)
            }
        })

        return () => {
            isActiveRequest = false;
            if (getChatRequestIdRef.current === requestId) {
                cancelGetChatRequest('Смена чата или темы');
            }
        };
    }, [chat, getActiveChatParams, isNeedUpdate]);

    // Обработчик вставки через Ctrl+V
    useEffect(() => {
        const handlePaste = (event) => {
            // Проверяем, что фокус в инпуте сообщения
            const isFocusInChat = !!event?.target?.closest(`.classForInsertingFiles`);
            
            if (!isFocusInChat) return;
            
            const items = event.clipboardData?.items;
            if (!items) return;
            
            // Ищем изображение среди вставленных элементов
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf('image') !== -1) {
                    const file = items[i].getAsFile();
                    if (file) {
                        event.preventDefault();
                        event.stopPropagation();
                        
                        // Устанавливаем файл и открываем попап
                        setFile(file);
                        setIsVisiblePopup(true);
                        break;
                    }
                }
            }
        };

        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, []);

    useEffect(() => {
        const joinCurrentChat = () => {
            websocket.send('join_chat', { chatId: chat, origin: window.location.origin });
        };

        const isCurrentChatEvent = (eventChatId) => String(eventChatId) === String(chat);

        const isCurrentTopicEvent = (eventTopicId) => {
            if (activeTopicId === null || activeTopicId === undefined) {
                return eventTopicId === null || eventTopicId === undefined;
            }

            return String(eventTopicId) === String(activeTopicId);
        };

        const isAllowedEventOrigin = (origin) => {
            if (!origin) return true;

            try {
                const originUrl = new URL(origin.replace(/(https?:\/\/[^:]+).*$/, '$1'));
                return originUrl.hostname === window.location.hostname;
            } catch (error) {
                return true;
            }
        };

        const unsubscribeOpen = websocket.subscribeOpen(joinCurrentChat);
        const unsubscribeConnectionStatus = websocket.subscribeConnectionStatus(({ status }) => {
            if (status === 'reconnecting' || status === 'unavailable') {
                socketCatchUpNeededRef.current = true;
                return;
            }

            if (status !== 'open' || !socketCatchUpNeededRef.current) return;

            // После reconnect добираем только события, которые могли потеряться,
            // вместо постоянного секундного опроса support-чата.
            socketCatchUpNeededRef.current = false;
            refreshLatestMessages().catch(() => undefined);
            if (data?.type === 'support') setIsNeedUpdate(true);
        });

        const unsubscribeChatMessage = websocket.subscribe('chat_message', (newMessage) => {
            if (!newMessage?.message) return;
            if (!isCurrentChatEvent(newMessage?.chatId)) return;
            if (!isCurrentTopicEvent(newMessage?.message?.topicId)) return;
            if (!isAllowedEventOrigin(newMessage?.message?.origin)) return;
            newMessage.message.origin = newMessage?.message?.origin || window.location.origin;

            if (newMessage) {
                const originUrl = new URL(newMessage.message.origin?.replace(/(https?:\/\/[^:]+).*$/, '$1')); //регуляркой убираем всё после второго двоеточия, если оно есть, включая само второе двоеточие
                const originDomain = originUrl.hostname

                if (originDomain !== window.location.hostname) return

                appendIncomingMessage(newMessage?.message);
                const messageSupportTicket = newMessage?.supportTicket || newMessage?.support_ticket || newMessage?.message?.supportTicket || newMessage?.message?.support_ticket;

                if (messageSupportTicket) {
                    setData((prev) => ({
                        ...prev,
                        supportTicket: prev?.type === 'support'
                            ? getSupportTicketFromEvent({
                                chatId: newMessage?.chatId,
                                supportTicket: messageSupportTicket,
                            }, prev?.supportTicket)
                            : prev?.supportTicket,
                    }));
                }

                if (shouldDisableAutoReadForCurrentChat) {
                    scrollToBottom();
                }
                else{
                    chatApi.readAllChatMessages(chat).then(() => {
                        scrollToBottom();
                        resetSupportUnreadCountIfNeeded();
                    });
                }
            }
        });

        const unsubscribeFileUploaded = websocket.subscribe('file_uploaded', (fileData) => {
            if (!fileData?.file || !fileData?.messageId) return;
            if (!isCurrentChatEvent(fileData?.chatId)) return;
            if (!isCurrentTopicEvent(fileData?.topicId)) return;
            // FIXME: добавить проверку на origin (как в chat_message)
            if (fileData) {
                appendUploadedFile(fileData.messageId, fileData.file);

                if (shouldDisableAutoReadForCurrentChat) {
                    scrollToBottom();
                }
                else{
                    chatApi.readAllChatMessages(chat).then(() => {
                        scrollToBottom();
                        resetSupportUnreadCountIfNeeded();
                    });
                }
            }
        });

        const unsubscribeChatMessageDeleted = websocket.subscribe('chat_message_deleted', (deletedMessage) => {
            if (!deletedMessage?.messageId) return;
            if (!isCurrentChatEvent(deletedMessage?.chatId)) return;
            if (!isCurrentTopicEvent(deletedMessage?.topicId)) return;

            removeMessage(deletedMessage.messageId);
            changeTopicMessagesCount(deletedMessage?.topicId ?? activeTopicId, -1);
        });

        const unsubscribeChatMessageReaction = websocket.subscribe('chat_message_reaction', (reactionEvent) => {
            if (!reactionEvent?.messageId) return;
            if (!isCurrentChatEvent(reactionEvent?.chatId)) return;
            if (!isCurrentTopicEvent(reactionEvent?.topicId)) return;

            updateMessageReactions(reactionEvent.messageId, reactionEvent?.reactions ?? []);
        });

        const unsubscribeChatTopicChanged = websocket.subscribe('chat_topic_changed', async (topicEvent) => {
            if (!isCurrentChatEvent(topicEvent?.chatId)) return;

            if (
                topicEvent?.action === 'deleted' &&
                String(topicEvent?.topicId ?? null) === String(activeTopicId ?? null)
            ) {
                pendingTopicMessageRef.current = null;
                pendingHighlightFromMessageIdRef.current = null;
                selectTopic(null);
                loadTopics();
                return;
            }

            await loadTopics();
        });

        const unsubscribeChatRead = websocket.subscribe('chat.read', (payload) => {
            if (!isGroupChat) return;

            const topicUnreadMessages = Array.isArray(payload?.data?.data?.topicUnreadMessages)
                ? payload.data.data.topicUnreadMessages
                : [];

            const currentChatTopicUnreadMessages = topicUnreadMessages.filter(
                (item) => String(item?.chatId) === String(chat),
            );

            applyTopicUnreadMessages(currentChatTopicUnreadMessages, {
                openedTopicId: activeTopicId,
            });
        });

        const unsubscribeSupportTicketChanged = websocket.subscribe('chat_support_ticket_changed', (payload) => {
            const ticketEvent = payload?.data?.chatId ? payload.data : payload;

            if (!isCurrentChatEvent(ticketEvent?.chatId)) return;

            setData((prev) => ({
                ...prev,
                supportTicket: getSupportTicketFromEvent(ticketEvent, prev?.supportTicket),
                supportStatus: ticketEvent?.supportStatus ?? prev?.supportStatus,
            }));
        });

        return () => {
            websocket.send('leave_chat', { chatId: chat, origin: window.location.origin });
            unsubscribeOpen?.();
            unsubscribeConnectionStatus?.();
            unsubscribeChatMessage?.();
            unsubscribeFileUploaded?.();
            unsubscribeChatMessageDeleted?.();
            unsubscribeChatMessageReaction?.();
            unsubscribeChatTopicChanged?.();
            unsubscribeChatRead?.();
            unsubscribeSupportTicketChanged?.();
        };
    }, [
        activeTopicId,
        applyTopicUnreadMessages,
        appendIncomingMessage,
        appendUploadedFile,
        changeTopicMessagesCount,
        chat,
        loadTopics,
        removeMessage,
        refreshLatestMessages,
        shouldDisableAutoReadForCurrentChat,
        selectTopic,
        updateMessageReactions,
        data?.type,
    ]);

    useEffect(() => {
        if (
            isLoadingInitialMessages ||
            !dataMessages.length ||
            isOlderMessagesScrollEnabled ||
            messagesViewMode === 'context' ||
            pendingScrollTargetId
        ) {
            return;
        }

        requestAnimationFrame(() => {
            scrollToBottom('auto');
            enableOlderMessagesScroll();
            resetSupportUnreadCountIfNeeded();
        });
    }, [
        dataMessages.length,
        enableOlderMessagesScroll,
        isLoadingInitialMessages,
        isOlderMessagesScrollEnabled,
        messagesViewMode,
        pendingScrollTargetId,
    ]);

    const clearingDataAfterTextMessage = () => {
        setAnswerTo(null)
        setAnswerAuthor(null)
        chatTextInputRef.current?.clear?.()
    }
    const clearingDataAfterFileMessage = () => {
        setIsVisiblePopup(false)
        setAction(false)
        setFile(null)
        resetRecording()
    }

    const addMes = (chatId, message, parentMessageId, file) => {
        setSaving(true)
        setProgress(0)
        const tempMessageId = appendMessage(message, parentMessageId)

        chatApi.createMessage({
            chatId: chatId,
            message: JSON.stringify(message),
            parentMessageId: parentMessageId,
            ...(activeTopicId === null || activeTopicId === undefined ? {} : { topicId: activeTopicId }),
        }, {
            onUploadProgress: function(progressEvent) {
                let percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                
                // setProgress(percentCompleted)
            },
        }).then(async (res) => {
            
            clearingDataAfterTextMessage()

            if (res.status === 200) {
                const createdMessage = res.data?.message ?? res.data;
                const createdMessageId = createdMessage?.id ?? res.data?.id;

                replaceTempMessage(tempMessageId, createdMessage)
                changeTopicMessagesCount(activeTopicId, 1)
                chatContext?.setLastMessageUpdate?.({
                    chatId,
                    ...(createdMessageId === undefined ? {} : { id: createdMessageId }),
                    message: createdMessage?.message ?? message,
                    createdAt: createdMessage?.createdAt ?? createdMessage?.created_at ?? new Date().toISOString(),
                    authorAvatar: createdMessage?.authorAvatar ?? createdMessage?.author_avatar ?? profile?.avatar,
                })

                if (
                    data?.type === 'support' &&
                    isTechSupport &&
                    !data?.supportTicket?.managerId &&
                    !data?.supportTicket?.manager?.id
                ) {
                    setIsNeedUpdate(true)
                }

                if (file && typeof file === 'object') {
                    if (!createdMessageId) {
                        addAlert({
                            type: 'error',
                            title: 'Файл не отправлен',
                            text: 'Не удалось получить id сообщения для загрузки файла',
                        });
                        return;
                    }

                    const formData = new FormData();
                    formData.append('file', file);
                    if(file?.type?.includes('audio') || (file?.name?.includes(nameAudioFile) && file?.name?.includes(`.${nameAudioExtension}`))){
                        formData.append('voice', 'true');
                    }
                    const source = getCancelTokenSource();
                    await chatApi.addMessageFile(createdMessageId, formData, {
                        onUploadProgress: function (progressEvent) {
                            let percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                            setProgress(percentCompleted)
                        },
                        cancelToken: source.token
                    }).then((fileRes) => {
                        const uploadedFile = fileRes?.data?.file
                            ?? fileRes?.data?.message?.files?.slice?.(-1)?.[0]
                            ?? fileRes?.data?.files?.slice?.(-1)?.[0];

                        if (uploadedFile) {
                            appendUploadedFile(createdMessageId, uploadedFile);
                        } else {
                            appendUploadedFile(createdMessageId, {
                                id: `local_${createdMessageId}_${file.name}`,
                                type: file.type,
                                path: URL.createObjectURL(file),
                                original: file.name,
                            });
                        }

                        clearingDataAfterFileMessage()
                    })
                }
            }
        }).finally(() => {
            setTimeout(() => {
                setSaving(false);
                setProgress(0);
            }, 1000)
        })
    }

    const appendMessage = (message, parentMessageId) => {
        const tempMessageId = appendTempMessage({
            author: data?.members?.find(member => member.user_id === profile.id)?.member_id,
            message,
            parentId: parentMessageId,
            topicId: activeTopicId,
            createdAt: (new Date()).getTime() / 1000,
            files: []
        })

        setTimeout(scrollToBottom, 50)
        return tempMessageId
    }

    const clearAnswer = () => {
        setAnswerTo(null)
        setAnswerAuthor(null)
    }

    const handleCloseFilePopup = () => {
        if (saving) {
            const currentSource = getCurrentSource();
            if (currentSource) {
                // cancelRequest('Загрузка остановлена пользователем'); // можно остановить загрузку
            }
        }

        setSaving(false)
        setIsVisiblePopup(false)
    }

    const getPreparedMessage = () => chatTextInputRef.current?.getSerializedValue?.() || message;

    const validatePdfForSending = async (selectedFile) => {
        const isPdf = selectedFile?.type === 'application/pdf'
            || String(selectedFile?.name || '').toLowerCase().endsWith('.pdf');

        if (!censorshipEnabled || !isPdf || !censorshipWords.length) return true;

        try {
            const pdfText = await readPdfText(selectedFile, { restoreWordGaps: true });

            // PDF без текстового слоя разрешаем отправлять: проверить его без OCR невозможно.
            if (!pdfText.trim()) return true;

            if (getCensorshipMatches(pdfText, censorshipWords).length) {
                addAlert({
                    type: 'error',
                    title: 'Файл не отправлен',
                    text: 'Файл содержит запрещённые слова',
                });
                return false;
            }

            return true;
        } catch (error) {
            console.error('Failed to check PDF content', error);
            addAlert({
                type: 'error',
                title: 'Файл не отправлен',
                text: 'Не удалось проверить содержимое файла',
            });
            return false;
        }
    };

    const submitCurrentMessage = (preparedMessage = getPreparedMessage()) => {
        if (shouldRequestSupportTicketType) {
            addAlert({
                type: 'error',
                title: 'Выберите тип обращения',
                text: 'После выбора типа обращения можно будет написать сообщение в поддержку.',
            });
            return;
        }

        const text = String(preparedMessage ?? '').trim();
        const hasAttachedFile = Boolean(file);

        if(!text && !hasAttachedFile){
            chatTextInputRef.current?.clear?.()
            return;
        }

        const forbiddenWords = censorshipEnabled && text
            ? getCensorshipMatches(preparedMessage, censorshipWords)
            : [];

        if (forbiddenWords.length && censorshipMode === CENSORSHIP_MODES.BLOCK) {
            addAlert({
                type: 'error',
                title: 'Сообщение не отправлено',
                text: `В сообщении есть запрещенные слова: ${forbiddenWords.join(', ')}`,
            });
            return;
        }

        const moderatedMessage = forbiddenWords.length
            ? applyCensorshipToText(preparedMessage, censorshipWords, censorshipMode)
            : preparedMessage ?? '';

        if (!hasAttachedFile && !String(moderatedMessage ?? '').replace(/<[^>]*>/g, ' ').trim()) {
            addAlert({
                type: 'error',
                title: 'Сообщение не отправлено',
                text: 'После применения цензуры сообщение стало пустым',
            });
            return;
        }

        addMes(data.id, moderatedMessage, answerTo?.id, file);
        chatTextInputRef.current?.clear?.()
    }

    const sendMessage = (e) => {
        e.preventDefault();
        submitCurrentMessage()
    }

    const [bindFile] = useInputNew({
        name: "file",
        value: file,
        onChange: (e) => setFile(e),
        placeholder: "",
    });

    const scrollToClip = async (messageId, nextTopicId = activeTopicId) => {
        if (!messageId) return;

        await openMessageContext(messageId, nextTopicId);
    }

    const handleSelectTopic = (topicId = null) => {
        pendingTopicMessageRef.current = null;
        selectTopic(topicId);
    }

    const handleCreateTopic = async (title) => {
        await createTopic(title);
    }

    const handleRenameTopic = async (params) => {
        await renameTopic(params);
        setIsNeedUpdate(true);
    }

    const handleDeleteTopic = async (topicId) => {
        await deleteTopic(topicId);
    }

    const handleToggleMessageReaction = async (messageId, reaction, currentReactions = []) => {
        if (!messageId || !reaction) return;

        const myReaction = currentReactions.find((item) => item?.reactedByMe);

        if (myReaction?.reaction === reaction) {
            const response = await chatApi.toggleMessageReaction(messageId, reaction);
            updateMessageReactions(messageId, response?.data?.reactions ?? []);
            return;
        }

        if (myReaction?.reaction) {
            const removeResponse = await chatApi.toggleMessageReaction(messageId, myReaction.reaction);
            updateMessageReactions(messageId, removeResponse?.data?.reactions ?? []);
        }

        const addResponse = await chatApi.toggleMessageReaction(messageId, reaction);
        updateMessageReactions(messageId, addResponse?.data?.reactions ?? []);
    }

    const handleSelectSearchMessage = async (searchMessage) => {
        if (!searchMessage?.id) return;

        const nextTopicId = searchMessage?.topicId ?? null;

        if (String(nextTopicId) !== String(activeTopicId)) {
            pendingTopicMessageRef.current = {
                messageId: searchMessage.id,
                topicId: nextTopicId,
            };
            selectTopic(nextTopicId);
            return;
        }

        await scrollToClip(searchMessage.id, nextTopicId);
    }

    useEffect(() => {
        if (action) {
            validatePdfForSending(file).then((isAllowed) => {
                if (isAllowed) {
                    addMes(data.id, getPreparedMessage(), answerTo?.id, file);
                } else {
                    setAction(false);
                }
            });
        }
    }, [action])

    const addFile = () => {
        setIsVisiblePopup(true)
    }
    const isCreator = data?.members?.filter(member => member.isCreator === 1)
    const creator = isCreator?.length === 1 ? isCreator[0].name : 'Вы'
    const fixedMessages = React.useMemo(() => {
        const messages = Array.isArray(data?.fixedMessages) ? data.fixedMessages : [];

        return messages.filter((message) => {
            const messageTopicId = message?.topicId ?? message?.topic_id ?? message?.topic?.id ?? null;

            return String(messageTopicId ?? null) === String(activeTopicId ?? null);
        });
    }, [activeTopicId, data?.fixedMessages]);
    const [activePinnedMessageIndex, setActivePinnedMessageIndex] = useState(0);
    const activePinnedMessage = fixedMessages[activePinnedMessageIndex] ?? fixedMessages[0];

    useEffect(() => {
        setActivePinnedMessageIndex(0);
    }, [activeTopicId, data?.fixedMessages]);

    const handlePinnedMessageClick = () => {
        if (!activePinnedMessage?.id) return;

        scrollToClip(activePinnedMessage.id, activeTopicId);
        setActivePinnedMessageIndex((currentIndex) => (
            fixedMessages.length > 1 ? (currentIndex + 1) % fixedMessages.length : 0
        ));
    };

    const handlePinnedMessageKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handlePinnedMessageClick();
        }
    };

    const handlePinnedMessageUnfix = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (!activePinnedMessage?.id) return;

        chatApi.unFixMessage(activePinnedMessage.id).then(() => {
            setIsNeedUpdate(true);
        });
    };
    const isChatDataReady = !!data && Array.isArray(data?.members);
    const isInitialChatContentReady =
        isChatDataReady &&
        !isLoadingInitialMessages;

    const chatData = {
        ...data,
        messages: dataMessages
    }

    const supportTicket = data?.supportTicket;
    const supportTicketManagerId = supportTicket?.managerId ?? supportTicket?.manager?.id;
    const supportTicketTypeId = supportTicket?.ticketTypeId
        ?? supportTicket?.ticket_type_id
        ?? supportTicket?.typeId
        ?? supportTicket?.type_id
        ?? supportTicket?.ticket_type?.id
        ?? supportTicket?.type?.id;
    const [helpdeskSettings, setHelpdeskSettings] = useState(DEFAULT_HELPDESK_SETTINGS);
    const [supportRating, setSupportRating] = useState(0);
    const [supportRatingComment, setSupportRatingComment] = useState('');
    const [supportRatingSent, setSupportRatingSent] = useState(false);
    const [supportTicketTypeSaving, setSupportTicketTypeSaving] = useState(false);
    const supportTicketTypeRequestOptions = React.useMemo(
        () => (helpdeskSettings.ticketTypes || [])
            .filter((type) => Boolean(type.askUserToSelectOnCreate))
            .sort((first, second) => {
                const firstNumber = Number(first.selectionNumber) || Number.MAX_SAFE_INTEGER;
                const secondNumber = Number(second.selectionNumber) || Number.MAX_SAFE_INTEGER;

                return firstNumber - secondNumber;
            }),
        [helpdeskSettings.ticketTypes]
    );
    const shouldShowSupportRating = data?.type === 'support' &&
        !isTechSupport &&
        Boolean(supportTicket?.isClosed) &&
        !Boolean(supportTicket?.ratingSubmitted) &&
        Boolean(helpdeskSettings.requestSupportRating);
    // Пока оценку ждут, но не поставили, новое обращение не предлагаем — иначе
    // выбор типа перекроет просьбу оценить закрытый тикет.
    const isSupportRatingPending = shouldShowSupportRating && !supportRatingSent;
    // Тикет закрыт и оценён (или оценка не запрашивается) — предлагаем открыть
    // следующее обращение. Выбор типа и есть точка входа в новый тикет.
    const canStartNewSupportTicket = data?.type === 'support' &&
        !isTechSupport &&
        Boolean(supportTicket?.isClosed) &&
        !isSupportRatingPending;
    const shouldRequestSupportTicketType = data?.type === 'support' &&
        !isTechSupport &&
        supportTicketTypeRequestOptions.length > 0 &&
        (
            canStartNewSupportTicket ||
            (Boolean(supportTicket?.id) && !Boolean(supportTicket?.isClosed) && !supportTicketTypeId)
        );
    const supportStatusOptions = React.useMemo(() => ([
        { value: 0, label: 'Без статуса' },
        ...supportStatuses.map((status) => ({
            value: status?.id,
            label: status?.title,
        })),
    ]), [supportStatuses]);
    const supportManagerOptions = React.useMemo(() => (
        techSupportUserIds.map((id) => {
            const manager = supportManagers.find((item) => Number(item?.id) === Number(id));

            return {
                value: id,
                label: manager ? (formateName(manager.name, 'F I') || manager.name || `ID ${id}`) : `ID ${id}`,
            };
        })
    ), [supportManagers, techSupportUserIds]);

    useEffect(() => {
        if (data?.type !== 'support') return undefined;

        let active = true;

        fetchHelpdeskSettings()
            .then((settings) => { if (active) setHelpdeskSettings(settings); })
            .catch(() => undefined);

        return () => { active = false; };
    }, [data?.type]);

    useEffect(() => {
        setSupportRating(0);
        setSupportRatingComment('');
        setSupportRatingSent(Boolean(supportTicket?.ratingSubmitted));
    }, [supportTicket?.id, supportTicket?.ratingSubmitted]);

    const handleSubmitSupportRating = async () => {
        if (!supportRating) {
            addAlert({ type: 'error', title: 'Выберите оценку' });
            return;
        }
        if (!supportTicket?.id) {
            addAlert({ type: 'error', title: 'Тикет не найден' });
            return;
        }

        try {
            await helpdeskAPI.rateTicket(supportTicket.id, {
                rating: supportRating,
                comment: supportRatingComment.trim(),
            });
            setSupportRatingSent(true);
        } catch (error) {
            addAlert({
                type: 'error',
                title: 'Не удалось отправить оценку',
                text: error?.response?.data?.error?.[0],
            });
        }
    };

    const handleSelectSupportTicketType = async (ticketType) => {
        if (!ticketType?.id || supportTicketTypeSaving) return;
        if (!supportTicket?.id && !canStartNewSupportTicket) return;

        setSupportTicketTypeSaving(true);

        try {
            let ticketId = supportTicket?.id;

            // Предыдущий тикет закрыт: выбор типа открывает новое обращение,
            // иначе тип ушёл бы в уже закрытый тикет.
            if (!ticketId || supportTicket?.isClosed) {
                const response = await chatApi.openSupportTicket(chat);
                const openedTicket = response?.data?.data?.ticket ?? response?.data?.ticket ?? null;

                ticketId = openedTicket?.id;

                if (!ticketId) {
                    throw new Error('Не удалось открыть новое обращение');
                }

                // Состояние оценки сбросит эффект по смене supportTicket.id.
                setData((prev) => ({
                    ...prev,
                    supportTicket: getSupportTicketFromEvent({ supportTicket: openedTicket }, null),
                }));
                setIsNeedUpdate(true);
            }

            await helpdeskAPI.updateTicket(ticketId, { ticketTypeId: ticketType.id });
            setData((prev) => ({
                ...prev,
                supportTicket: {
                    ...(prev?.supportTicket || {}),
                    ticketTypeId: ticketType.id,
                    ticket_type_id: ticketType.id,
                    ticket_type: {
                        ...(prev?.supportTicket?.ticket_type || {}),
                        id: ticketType.id,
                        title: ticketType.name,
                        name: ticketType.name,
                    },
                },
            }));
        } catch (error) {
            addAlert({
                type: 'error',
                title: 'Не удалось выбрать тип обращения',
                text: error?.response?.data?.error?.[0] ?? error?.message,
            });
        } finally {
            setSupportTicketTypeSaving(false);
        }
    };

    const handleChangeSupportStatus = async (statusId) => {
        const response = await chatApi.setSupportChatStatus(chat, {
            status_id: statusId,
        });

        if (response?.data?.status !== 'success') {
            addAlert({ type: 'error', title: 'Не удалось изменить статус чата' });
            return;
        }

        const nextSupportStatus = statusId === 0
            ? null
            : supportStatuses.find((status) => Number(status?.id) === Number(statusId)) ?? null;

        setData((prev) => ({
            ...prev,
            supportStatus: response?.data?.data?.supportStatus ?? nextSupportStatus,
        }));
    };

    const handleAssignSupportTicketManager = async (managerId) => {
        const response = await chatApi.assignSupportTicketManager(chat, {
            manager_id: managerId,
        });

        if (response?.data?.status !== 'success') {
            addAlert({ type: 'error', title: 'Не удалось назначить менеджера' });
            return;
        }

        const manager = supportManagers.find((item) => Number(item?.id) === Number(managerId)) ?? null;

        setData((prev) => ({
            ...prev,
            supportTicket: {
                ...(prev?.supportTicket || {}),
                managerId,
                manager: response?.data?.data?.supportTicket?.manager ?? manager,
                isClosed: false,
            },
        }));
    };

    const handleCloseSupportTicket = async () => {
        const response = await chatApi.closeSupportTicket(chat);

        if (response?.data?.status !== 'success') {
            addAlert({ type: 'error', title: 'Не удалось закрыть тикет' });
            return;
        }

        const responseTicket = response?.data?.data?.supportTicket || response?.data?.supportTicket || null;

        setData((prev) => ({
            ...prev,
            supportTicket: {
                ...(prev?.supportTicket || {}),
                ...(responseTicket || {}),
                isClosed: true,
                ratingSubmitted: Boolean(responseTicket?.ratingSubmitted ?? responseTicket?.rating_submitted ?? prev?.supportTicket?.ratingSubmitted),
                closedAt: responseTicket?.closedAt ?? responseTicket?.closed_at ?? prev?.supportTicket?.closedAt ?? new Date().toISOString(),
            },
        }));
        setIsNeedUpdate(true);
    };

    const HEIGHT_DIFFER = 4;
    const SCROLL_HEIGHT = height/* - 200*/;
    
    const scrollToBottom = (behavior = 'smooth') => {
        messageEndRef?.current?.scrollIntoView({behavior})
    }

    const handleScrollToBottom = async () => {
        const shouldReturnToLatest =
            messagesViewMode === 'context' || hasNewerMessages;

        if (shouldReturnToLatest) {
            await returnToLatestMessages(activeTopicId);

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    scrollToBottom('auto');
                });
            });
            return;
        }

        scrollToBottom();
    }

    const handleMessagesForwarded = (forwarded = []) => {
        const lastMessageUpdates = forwarded.map((item) => {
            const lastMessage = item?.messages?.[item.messages.length - 1];

            if (!lastMessage) return null;

            return {
                chatId: item.chatId,
                id: lastMessage.id,
                message: lastMessage.message ?? '',
                createdAt: lastMessage.createdAt,
                authorAvatar: profile?.avatar,
            };
        }).filter(Boolean);

        if (lastMessageUpdates.length) {
            chatContext?.setLastMessageUpdate?.(lastMessageUpdates);
        }

        const currentChatResult = forwarded.find(
            (item) => String(item?.chatId) === String(chat),
        );
        const isRootChatOpen = activeTopicId === null || activeTopicId === undefined;

        if (!currentChatResult || !isRootChatOpen) return;

        const forwardedMessages = currentChatResult.messages || [];
        forwardedMessages.forEach(appendIncomingMessage);

        if (messagesViewMode === 'context' || hasNewerMessages) return;

        requestAnimationFrame(() => {
            requestAnimationFrame(() => scrollToBottom('auto'));
        });
    }

    useEffect(() => {
        if (!pendingScrollTargetId || !messagesContainerRef.current) return;

        const hasScrolled = scrollMessageToCenter(
            messagesContainerRef.current,
            pendingScrollTargetId,
        );

        if (hasScrolled) {
            setHighlightedMessageId(pendingScrollTargetId);
            if (String(pendingHighlightFromMessageIdRef.current ?? '') === String(pendingScrollTargetId)) {
                setHighlightedMessagesFromId(pendingScrollTargetId);
            }
            else{
                setHighlightedMessagesFromId(null);
            }
            clearTimeout(highlightTimeoutRef.current);
            highlightTimeoutRef.current = setTimeout(() => {
                setHighlightedMessageId(null);
                setHighlightedMessagesFromId(null);
            }, 2200);
            pendingHighlightFromMessageIdRef.current = null;
            clearPendingScrollTarget();
        }
    }, [clearPendingScrollTarget, dataMessages, pendingScrollTargetId]);

    useEffect(() => {
        return () => {
            clearTimeout(highlightTimeoutRef.current);
        };
    }, []);

    const onDeleteMessage = (message) => {
        removeMessage(message.id)
        changeTopicMessagesCount(message?.topicId ?? activeTopicId, -1)
    }

    const handleCollapseBtnClick = () => setPage(
        'default',
        null,
        'create',
        isLessonChat ? params : undefined,
    )
    const goToMainPage = () => {
        if(data?.type === 'support' && isTechSupport){
            setPage('main', null, 'create', { selectedTab: 'support' })
        }
        else if(params?.selectedTab === 'lesson'){
            setPage('main', null, 'create', params)
        }
        else{
            setPage('main')
        }
    }
    const [isActiveMenuInHeader, setIsActiveMenuInHeader] = useState(false);

    const toggleMenuInHeader = (e) => {
        e.stopPropagation()
        setIsActiveMenuInHeader(prev => !prev)
    }

    const toggleReadState = () => {
        chatApi.read(chat, {read: false}).then(({data})=>{ 
            if(data.status === 'success'){
                if(chatIsDisabledButTechnicalSupportIsEnabled && !isTechSupport){
                    setPage('default')
                }
                else{
                    goToMainPage()
                }
            }
        })
    }

    const markAsRead = () => {
        chatApi.readAllChatMessages(chat, {read: true}).then(() => {
            if (data?.type === 'support' && setUnreadSupportMessagesCount) {
                setUnreadSupportMessagesCount(0);
            }

            if(chatIsDisabledButTechnicalSupportIsEnabled && !isTechSupport){
                setPage('default')
            }
            else{
                goToMainPage()
            }
        })
    }
    
    const dispatch = useDispatch();
    
    const openModalDelayedMessage = async ({ addFile }) => {
        if (addFile && !await validatePdfForSending(file)) return;

        dispatch(openModal({
            type: TYPES.DELAYED_MESSAGE,
            data: {
                chatId: data.id,
                message: getPreparedMessage(),
                parentMessageId: answerTo?.id,
                topicId: activeTopicId,
                file: addFile ? file : null,
                onSuccess: () => {
                    clearingDataAfterTextMessage()
                    getDelayedMessages()
                    addFile && clearingDataAfterFileMessage()
                }
            }
        }))
    }

    const {
          isRecording,
          audioURL,
          audioFile,
          startRecording,
          stopRecording,
          resetRecording,
          hasAudio
    } = useAudioRecorder();

    useEffect(()=>{
        if(hasAudio && audioFile){
            addFile()
            setFile(audioFile);
        }
    }, [hasAudio, audioFile])
    return (
        <div className={s.page} style={{height: `${height - HEIGHT_DIFFER}px`}}>
            <div className={s.header}>
                {
                    (
                        isShowDelayedMessages ||
                        (
                            !shouldHideLessonBackButton &&
                            (isTechSupport || !chatIsDisabledButTechnicalSupportIsEnabled)
                        )
                    ) && (
                        <button 
                            type={'button'} 
                            className={s.btn} 
                            onClick={isShowDelayedMessages ? toggleIsShowDelayedMessages : goToMainPage}>
                                <ArrowLeftIcon/>
                        </button>
                    )
                } 
                
                <div className={s.headerInfo}>
                    {(data?.members?.length > 2 || data?.type == 'group') ? <UserImage path={data?.avatar}/> : ''}
                    <div className={s.headerContent}>
                        <div className={s.title}>
                            {titleChat && (data?.type !== 'support' || !isTechSupport) && titleChat}
                            {
                                titleChat && data?.type === 'support' && isTechSupport && (
                                    <span onClick={() => setEditChildren(idInterlocutor)}>{titleChat}</span>
                                )
                            }
                            {!titleChat && 'Чат'}
                            {data?.type !== 'support' && (data?.members?.length > 2 || data?.type == 'group') && (
                                <div className={s.participants}>{data?.members?.length} {getRightWord(data?.members?.length, ['участник', 'участникa', 'участников'])}</div>
                            )}
                        </div>
                    </div>
                </div>
                {isInitialChatContentReady && !isShowDelayedMessages && (
                    <button
                        type="button"
                        className={classNames(s.headerIconButton, {
                            [s.headerIconButtonActive]: activeTopPanel === 'messageSearch',
                        })}
                        aria-label="Поиск по сообщениям"
                        onClick={() => setActiveTopPanel((prev) => prev === 'messageSearch' ? null : 'messageSearch')}>
                        <IconSearch />
                    </button>
                )}
                <div className={s.menuInHeaderWrapper}>
                    <div className={s.dots} onClick={(e) => toggleMenuInHeader(e)}><DotsIcon/></div>
                    <div 
                        className={classNames(s.menuInHeader, s.menu, {[s.active]: isActiveMenuInHeader})} 
                        onMouseLeave={() => setIsActiveMenuInHeader(false)}>
                        {
                            (data?.members?.length !== 2 || data?.type == 'group') && !shouldHideParticipantsSettingsMenuItem && (
                                <div
                                    onClick={() => setPage('info', chat, 'create', params)}
                                    className={s.menuInHeaderRow}
                                >
                                    <div className={s.menuInHeaderName}>Участники / настройки чата</div>
                                </div>
                            )
                        }
                        <div className={s.menuInHeaderRow} onClick={toggleReadState}>
                            <div className={s.menuInHeaderName}>Отметить непрочитанным</div>
                        </div>
                        {shouldDisableAutoReadForCurrentChat && (
                            <div className={s.menuInHeaderRow} onClick={markAsRead}>
                                <div className={s.menuInHeaderName}>Отметить прочитанным</div>
                            </div>
                        )}
                    </div>
                </div>
                {(!isFullPage || isLessonChat) && (
                    <button
                        type="button"
                        className={s.headerIconButton}
                        title={isLessonChat ? 'Закрыть чат' : 'Свернуть чат'}
                        aria-label={isLessonChat ? 'Закрыть чат' : 'Свернуть чат'}
                        onClick={handleCollapseBtnClick}
                    >
                        <CloseIcon width="16" height="16" />
                    </button>
                )}
                {data?.type === 'support' && isTechSupport && (
                    <div
                        className={classNames(s.supportTicketHeader, 'new-design-scroll')}
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                    >
                        <div className={s.wpapper}>
                            {!!supportManagerOptions.length && !supportTicket?.isClosed && (
                                <CompactSelect
                                    value={supportTicketManagerId}
                                    options={supportManagerOptions}
                                    placeholder="Выберите менеджера"
                                    onChange={handleAssignSupportTicketManager}
                                    className={s.supportHeaderManagerSelect}
                                />
                            )}
                            <CompactSelect
                                value={data?.supportStatus?.id ?? 0}
                                options={supportStatusOptions}
                                onChange={handleChangeSupportStatus}
                                className={s.supportHeaderStatusSelect}
                            />
                            {!!supportTicket && !supportTicket?.isClosed && (
                                <button
                                    type="button"
                                    className={s.closeTicketButton}
                                    onClick={handleCloseSupportTicket}
                                >
                                    Закрыть тикет
                                </button>
                            )}
                        </div>
                        
                        <div className={s.wpapper}>
                            <span className={s.supportTicketHeaderText}>
                                {getSupportTicketText(supportTicket)}
                            </span>
                            <span className={s.supportTicketHeaderText}>
                                Менеджер: {getSupportTicketManagerName(supportTicket)}
                            </span>
                        </div>
                    </div>
                )}
            </div>
            {isTopicsVisible && !isShowDelayedMessages && (
                <ChatTopics
                    topics={preparedTopics}
                    activeTopicId={activeTopicId}
                    isLoading={isLoadingTopics}
                    isAdmin={isChatAdmin}
                    onSelectTopic={handleSelectTopic}
                    onCreateTopic={handleCreateTopic}
                    onRenameTopic={handleRenameTopic}
                    onDeleteTopic={handleDeleteTopic}
                />
            )}
            {activeTopPanel === 'messageSearch' && isInitialChatContentReady && !isShowDelayedMessages && (
                <MessageSearchPanel
                    className={s.floatingSearchPanel}
                    chatId={chat}
                    members={data?.members || []}
                    onSelectMessage={handleSelectSearchMessage}
                    standalone
                />
            )}
            {!!activePinnedMessage && isInitialChatContentReady && !isShowDelayedMessages && (
                <div
                    role="button"
                    tabIndex={0}
                    className={s.pinnedBar}
                    onClick={handlePinnedMessageClick}
                    onKeyDown={handlePinnedMessageKeyDown}>
                    <span className={s.pinnedAccent} />
                    <span className={s.pinnedContent}>
                        <span className={s.pinnedTitle}>Закрепленное сообщение</span>
                        <span className={s.pinnedPreview}>{getChatMessagePreview(activePinnedMessage)}</span>
                    </span>
                    {fixedMessages.length > 1 && (
                        <span className={s.pinnedCount}>{fixedMessages.length}</span>
                    )}
                    {isChatAdmin && (
                        <span
                            role="button"
                            tabIndex={0}
                            className={s.pinnedUnfixButton}
                            title="Открепить сообщение"
                            onClick={handlePinnedMessageUnfix}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    handlePinnedMessageUnfix(event);
                                }
                            }}>
                            <CloseIcon width={'12'} height={'12'} />
                        </span>
                    )}
                </div>
            )}

            {
                !isShowDelayedMessages && (
                    <div
                        ref={messagesContainerRef}
                        onScroll={handleMessagesScroll}
                        className={classNames('new-design-scroll', 'auto-height-chat-in-page', s.messages)}
                        style={{height: `${SCROLL_HEIGHT}px`}}>
                        {isLoadingInitialMessages && (
                            <div className={s.preloaderWrapper}><Preloader /></div>
                        )}
                        {isLoadingMessageContext && (
                            <div className={s.preloaderWrapper}><Preloader /></div>
                        )}
                        {messagesViewMode === 'context' && !isLoadingMessageContext && (
                            <div className={s.defaultMessage} onClick={returnToLatestMessages}>
                                Вернуться к последним сообщениям
                            </div>
                        )}
                        {isLoadingOlderMessages && !isLoadingMessageContext && (
                            <div className={s.olderMessagesPreloader}><Preloader /></div>
                        )}
                        {!hasOlderMessages && activeTopicId === null && (
                            <div className={s.defaultMessage}>
                                {creator} создали {data?.name?.length > 0 ? data?.name : 'чат'}
                            </div>
                        )}
                        {!isLoadingInitialMessages && !isChatDataReady ? (
                            <div className={s.preloaderWrapper}><Preloader /></div>
                        ) : dataMessages && dataMessages.map((chat, index, array) => {
                            const previousDate = index > 0 ? array[index - 1]?.createdAt : null;
                            const currentDate = chat?.createdAt;


                            return <React.Fragment key={chat.id}>
                                <NewMessagesDateView 
                                    previousDate={previousDate} 
                                    currentDate={currentDate} 
                                />
                                <div
                                    data-chat-message-id={chat.id}
                                    className={classNames({
                                        [s.highlightedMessage]:
                                            String(highlightedMessageId) === String(chat.id) ||
                                            (
                                                highlightedMessagesFromId !== null &&
                                                Number(chat.id) >= Number(highlightedMessagesFromId)
                                            ),
                                    })}>
                                    <ChatMessage
                                        message={chat}
                                        data={chatData}
                                        setAnswer={setAnswerTo}
                                        setAnswerAuthor={setAnswerAuthor}
                                        activeUsers={activeUsers}
                                        setPage={setPage}
                                        setIsNeedUpdate={setIsNeedUpdate}
                                        onDeleteMessage={onDeleteMessage}
                                        onToggleReaction={handleToggleMessageReaction}
                                        onForwardMessage={setMessageToForward}
                                    />
                                </div>
                            </React.Fragment>
                        })}
                        <div className={s.messageEnd} ref={messageEndRef}></div>
                    </div>
                )
            }
            
            {
                isShowDelayedMessages && (
                    <div className={classNames('new-design-scroll', s.messages)} style={{height: `${SCROLL_HEIGHT}px`}}> 
                        {
                            loadingDelayedMessages ? (
                                <div className={s.preloaderWrapper}><Preloader /></div>
                            ) : (
                                <>
                                    { 
                                        delayedMessages.map((message) => {
                                            return <DelayedMessage key={message.id} {...{message, getDelayedMessages}} />
                                        })
                                    }
                                </>
                            )
                        }
                    </div>
                )
            }

            {shouldShowSupportRating && (
                <SupportTicketRating
                    rating={supportRating}
                    comment={supportRatingComment}
                    sent={supportRatingSent}
                    onRatingChange={setSupportRating}
                    onCommentChange={setSupportRatingComment}
                    onSubmit={handleSubmitSupportRating}
                />
            )}
            
            <div className={s.footer}>
                {answerTo && answerAuthor && <div className={s.answerTo}>
                    <div className={s.answerToWrapper}>
                        <div className={s.answerName}>{answerAuthor.name}</div>
                        <div className={s.answerPreviewText}>
                            {getChatMessagePreview(answerTo)}
                        </div>
                        <button type="button" className={s.close} onClick={clearAnswer}>
                            <CloseIcon width={'24'} height={'24'}/>
                        </button>
                    </div>
                </div>}
                {shouldRequestSupportTicketType && (
                    <div className={s.supportTicketTypeRequest}>
                        <div className={s.supportTicketTypeHeader}>
                            <strong>Выберите тип обращения</strong>
                            <span>{helpdeskSettings.ticketTypeSelectionText || 'Это поможет быстрее направить вопрос нужному специалисту.'}</span>
                        </div>
                        <div className={s.supportTicketTypeButtons}>
                            {supportTicketTypeRequestOptions.map((type) => (
                                <button
                                    key={type.id}
                                    type="button"
                                    disabled={supportTicketTypeSaving}
                                    onClick={() => handleSelectSupportTicketType(type)}
                                >
                                    {type.name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
                <div className={s.footerForm}>
                    {
                        isChatReader ? <div className={s.textFooter}>Вы являетесь читателем этого чата</div> : (
                            <form className={s.form} onSubmit={sendMessage}>
                                <button type={'button'} className={s.clipBtn} onClick={addFile} disabled={shouldRequestSupportTicketType}>
                                    <PaperClipIcon witdh={'32'} height={'32'}/>
                                </button>
                                
                                {isActive && <div className={classNames(s.menu, isActive ? s.active : '')}>
                                    <div className={s.menuRow}>
                                        <div className={s.menuRowItem}>
                                            <InputNew.InputFileNewDesign
                                                className={s.inputFile}
                                                {...bindFile}
                                                newDesign
                                                isFileIcon
                                                placeholder='Файл'
                                                icon='file'
                                            />
                                        </div>
                                    </div>
                                </div>}
                                <TooltipWrapper
                                    placement='top'
                                    title={shouldRequestSupportTicketType ? 'Выберите тип обращения, чтобы продолжить пользоваться чатом' : ''}
                                    className={s.blockedChatInputTooltip}
                                >
                                    <div className={s.blockedChatInputTooltip}>
                                        <ChatTextInput
                                    ref={chatTextInputRef}
                                    members={data?.members || []}
                                    value={message}
                                    onChange={setMessage}
                                    onSubmit={submitCurrentMessage}
                                    disabled={shouldRequestSupportTicketType}
                                    placeholder={shouldRequestSupportTicketType ? 'Выберите тип обращения' : 'Напишите сообщение'}
                                        />
                                    </div>
                                </TooltipWrapper>
                                
                                <button 
                                    onClick={isRecording ? stopRecording : startRecording}
                                    className={classNames(s.microphone, {[s.microphoneActive]: isRecording})}
                                    disabled={shouldRequestSupportTicketType}
                                    type='button'>
                                        {isRecording ? <MicrophoneIconActive /> : <MicrophoneIcon />}
                                </button> 
                                {
                                    thereIsDelayedMessages && (
                                        <button 
                                            type='button'
                                            className={
                                                classNames(
                                                    s.buttonShowDelayedMessages,
                                                    {[s.svgDarkBlue]: isShowDelayedMessages}
                                                )
                                            }
                                            onClick={toggleIsShowDelayedMessages}>
                                                <ScheduleIcon className={s.svgGrey} />
                                        </button>
                                    )
                                }
                                <div className={classNames(s.submitWrapper, {[s.noActiveSubmitWrapper]: !String(message ?? '').trim() && !file}, 'submit-chat-page')}>
                                    <button type={'submit'} disabled={shouldRequestSupportTicketType}><img src={sendIcon} alt="Отправить"/></button>
                                    <SendLater disabled={shouldRequestSupportTicketType} onClick={() => openModalDelayedMessage({addFile: false}) } />
                                </div>
                                
                            </form>
                        )
                    }
                </div>
            </div>
            {isVisiblePopup && <Popup
                isVisiblePopup={isVisiblePopup}
                closeCallback={handleCloseFilePopup}
                header={!!file ? file?.type?.includes('audio') ? 'Отправить аудио' : 'Отправить файл' : 'Выберите фото или файл'}
                updateFile={true}
                setAction={setAction}
                setFile={setFile}
                file={file}
                uploading={saving}
                progress={progress}
                moreButton={ <SendLater onClick={() => openModalDelayedMessage({addFile: true}) } /> }
                textButtom='Отправить'
            />}
            {dataMessages?.length > 6 &&
                <button
                    type={'button'}
                    className={classNames(
                        s.btnScroll,
                        answerTo && s.btnScrollWithAnswer,
                        shouldRequestSupportTicketType && s.btnScrollWithTicketTypeRequest,
                    )}
                    onClick={handleScrollToBottom}>
                    <ArrowLeftIcon/>
                </button>}

            <EditEntityModal
                open={!!dataForEditEntityModal?.userId}
                {
                    ...dataForEditEntityModal
                }
                onDismiss={() => setEditChildren(null)} 
            />
            {!!messageToForward && (
                <ForwardMessagesModal
                    message={messageToForward}
                    onClose={() => setMessageToForward(null)}
                    onForwarded={handleMessagesForwarded}
                />
            )}
        </div>
    )
}

const NewMessagesDateView = ({previousDate, currentDate}) => {
    // if(typeof previousDate !== 'string') return
    if(typeof currentDate !== 'string') return

    let isDifferent = false;
    let showingDateString = ""

    try {
        const previous = new Date(previousDate)
        const current = new Date(currentDate)

        isDifferent = !previous || current.toDateString() !== previous.toDateString()

        if(isDifferent){
            const date = createDate({date: current});
            showingDateString = `${date.dayNumber} ${date.monthGenitive}`;
        }
    } catch (error) {
        console.log(error)
    }
    
    if(isDifferent){
        return (
            <div className={s.newDayView}>
                <span>{showingDateString}</span>
            </div>
        )
    }        
}

const SupportTicketRating = ({ rating, comment, sent, onRatingChange, onCommentChange, onSubmit }) => {
    if (sent) {
        return (
            <div className={classNames(s.supportRating, s.supportRatingSuccess)}>
                <strong>Ваш ответ принят, спасибо!</strong>
            </div>
        );
    }

    return (
        <div className={s.supportRating}>
            <div className={s.supportRatingHeader}>
                <strong>Оцените работу поддержки</strong>
                <span>Нам важно понять, помог ли ответ.</span>
            </div>
            <div className={s.supportRatingStars} aria-label="Оценка поддержки">
                {[1, 2, 3, 4, 5].map((value) => (
                    <button
                        key={value}
                        type="button"
                        className={classNames(value <= rating && s.supportRatingStarActive)}
                        onClick={() => onRatingChange(value)}
                        aria-label={`Поставить ${value}`}
                    >
                        {value <= rating ? <StarFilled /> : <StarOutlined />}
                    </button>
                ))}
            </div>
            <textarea
                value={comment}
                onChange={(event) => onCommentChange(event.target.value)}
                placeholder="Пожалуйста, оставьте комментарий"
                rows={2}
            />
            <button type="button" onClick={onSubmit}>Отправить оценку</button>
        </div>
    );
};

export default InChatView;


const SendLater = ({onClick, disabled = false}) => {
    return (
        <TooltipWrapper placement='left' title='Отправить позже'>
            <button
                onClick={onClick}
                disabled={disabled}
                type='button'>
                <AlarmIcon className={s.svgGrey} style={{display: 'flex'}} />
            </button>
        </TooltipWrapper>
    )
}
